import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { formatApiError } from "../lib/api";
import { Eye, EyeOff, Lock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";
const BG_URL = "https://static.prod-images.emergentagent.com/jobs/46ba8d0f-50d1-45b0-932d-164e40ecbfdf/images/50735e6e5c6e778395b7d56d471708bfb2adeb4a99eaa2d7563eb80d96dda51a.png";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();

    // Redirect already-logged-in users (must be in useEffect, not during render)
    useEffect(() => {
        if (user) {
            if (user.role === "admin") navigate("/admin", { replace: true });
            else if (user.role === "coach") navigate("/coach", { replace: true });
            else navigate("/assessment", { replace: true });
        }
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return; // prevent double-submit
        setError("");
        setLoading(true);
        try {
            const data = await login(email.trim(), password);
            if (data.role === "admin") navigate("/admin");
            else if (data.role === "coach") navigate("/coach");
            else navigate("/assessment");
        } catch (err) {
            // Handle both API errors and network errors
            if (err.response) {
                setError(formatApiError(err.response.data?.detail));
            } else if (err.request) {
                setError("Unable to reach the server. Please check your connection and try again.");
            } else {
                setError("Login failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" style={{ background: "#F4F6F9" }}>
            {/* Left - Background Image */}
            <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden">
                <img src={BG_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2B5A8C 50%, #1E3A5F 100%)", opacity: 0.92 }} />
                <div className="relative z-10 text-center p-12 max-w-lg">
                    <img src={LOGO_URL} alt="K2B Learning" className="h-20 mx-auto mb-8" />
                    <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "Outfit" }}>
                        Coachability Diagnostic Platform
                    </h1>
                    <p className="text-white/70 text-base leading-relaxed">
                        A psychology-driven assessment for leaders, coaches & organizations.
                        Measure what matters in leadership development.
                    </p>
                </div>
            </div>

            {/* Right - Login Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <Card className="w-full max-w-md border-border bg-card" data-testid="login-card">
                    <CardHeader className="space-y-4 text-center pb-2">
                        <div className="lg:hidden flex justify-center mb-4">
                            <img src={LOGO_URL} alt="K2B Learning" className="h-14" />
                        </div>
                        <h2 className="text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                            Welcome Back
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Sign in to access your dashboard
                        </p>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div data-testid="login-error" className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
                                <Input
                                    data-testid="login-email-input"
                                    id="email" type="email" value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className="bg-background border-border"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-sm text-muted-foreground">Password</Label>
                                <div className="relative">
                                    <Input
                                        data-testid="login-password-input"
                                        id="password" type={showPw ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        className="bg-background border-border pr-10"
                                        required
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw(!showPw)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                data-testid="login-submit-btn"
                                type="submit" className="w-full"
                                disabled={loading}
                                style={{ backgroundColor: "#4A7AB5" }}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Signing in...
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Lock className="h-4 w-4" />
                                        Sign In
                                    </div>
                                )}
                            </Button>
                            <div className="text-center">
                                <a href="/forgot-password" data-testid="forgot-password-link"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors">
                                    Forgot your password?
                                </a>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
